import { useCallback, useState, type ChangeEvent, type FormEvent } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'

import { emptySearch, writeSearchState } from '@/shared/search/search-state'
import { cn, TextField } from '@/shared/ui'
import { Search } from '@/shared/ui/icons'

/**
 * A details page's search field, from the mockups' `details/HeaderSearchField.jsx`: a compact
 * field in the header that, on Enter, opens the matching search page (`to`) with the text as its
 * free-text search, as legacy's "<Record> details > Search" did.
 */
export function HeaderSearchField(props: {
  /** The search page's path, such as `/site-management/users`. */
  readonly to: string
  readonly placeholder: string
  /**
   * At least `w-56`, as the mockup has it, and wide enough for the whole placeholder in any
   * language, never wider than the header allows; a class here sets the width instead.
   */
  readonly className?: string
}) {
  const intl = useIntl()
  const navigate = useNavigate()
  const label = intl.formatMessage({ id: 'details.search.label' })
  const [value, setValue] = useState('')
  const { to } = props
  const change = useCallback((event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value), [])
  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      const query = writeSearchState({ ...emptySearch, q: value }).toString()
      void navigate(query === '' ? to : `${to}?${query}`)
    },
    [navigate, to, value],
  )

  return (
    // Named, so it stands apart from another search on the page, such as the skills list's.
    <search aria-label={label} className="min-w-0">
      <form onSubmit={submit} className={cn('max-w-full', props.className === undefined && 'grid w-fit min-w-56')}>
        {props.className === undefined && (
          // Sizes the field to its placeholder as drawn, in any language, in the field's own font and
          // padding, with a few px spare so rounding never clips the last glyph; never past the
          // header, where the field shrinks instead.
          <span aria-hidden="true" className="invisible col-start-1 row-start-1 h-0 overflow-hidden whitespace-nowrap border pl-10 pr-5 text-sm">
            {props.placeholder}
          </span>
        )}
        <TextField
          label={label}
          hideLabel
          icon={Search}
          type="search"
          size="compact"
          value={value}
          onChange={change}
          placeholder={props.placeholder}
          className={cn('col-start-1 row-start-1 max-w-full', props.className ?? 'w-full')}
        />
      </form>
    </search>
  )
}
